import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import { ShieldCheck, Shield, TriangleAlert, RefreshCw, CircleCheck } from 'lucide-react';
import type { TurnstileRenderOptions } from '../vite-env';

export interface TurnstileWidgetRef {
  reset: () => void;
  remove: () => void;
  getResponse: () => string | undefined;
}

export interface TurnstileWidgetProps {
  ref?: React.Ref<TurnstileWidgetRef>;
  siteKey?: string;
  action?: string;
  cData?: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  onSuccess: (token: string) => void;
  onError?: (errorCode?: string) => void;
  onExpire?: () => void;
  className?: string;
}

// User's official Cloudflare Turnstile Site Key from Dashboard (0x4AAAAAAFLhILYKdp64I0Y2)
export const TURNSTILE_SITE_KEY = 
  (typeof import.meta !== 'undefined' && 
   import.meta.env?.VITE_TURNSTILE_SITE_KEY && 
   !import.meta.env.VITE_TURNSTILE_SITE_KEY.includes('0x4AAAAAAFLhID') &&
   !import.meta.env.VITE_TURNSTILE_SITE_KEY.includes('0x4AAAAAAFLhIK') &&
   import.meta.env.VITE_TURNSTILE_SITE_KEY.trim() !== '')
    ? import.meta.env.VITE_TURNSTILE_SITE_KEY
    : '0x4AAAAAAFLhILYKdp64I0Y2';

export const TurnstileWidget = ({
  ref,
  siteKey = TURNSTILE_SITE_KEY,
  action = 'contact',
  cData,
  theme = 'dark',
  size = 'flexible',
  onSuccess,
  onError,
  onExpire,
  className = '',
}: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'verified' | 'error' | 'expired'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Expose reset, remove, and getResponse methods to parent forms
  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current) {
        try {
          window.turnstile.reset(widgetIdRef.current);
          setStatus('ready');
          setErrorMessage(null);
        } catch (err) {
          console.warn('[Turnstile] Reset notice:', err);
        }
      } else {
        setStatus('ready');
      }
    },
    remove: () => {
      if (window.turnstile && widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        } catch (err) {
          console.warn('[Turnstile] Remove notice:', err);
        }
      }
    },
    getResponse: () => {
      if (window.turnstile && widgetIdRef.current) {
        return window.turnstile.getResponse(widgetIdRef.current);
      }
      return undefined;
    }
  }));

  useEffect(() => {
    let isMounted = true;
    let pollInterval: any = null;

    // Ensure Turnstile script is loaded in document
    const ensureScriptLoaded = (): Promise<void> => {
      return new Promise((resolve) => {
        if (window.turnstile) {
          resolve();
          return;
        }

        const scriptId = 'cf-turnstile-script';
        let script = document.getElementById(scriptId) as HTMLScriptElement | null;

        if (!script) {
          script = document.createElement('script');
          script.id = scriptId;
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
        }

        pollInterval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(pollInterval);
            pollInterval = null;
            resolve();
          }
        }, 100);

        setTimeout(() => {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          resolve();
        }, 5000);
      });
    };

    const renderWidget = async () => {
      await ensureScriptLoaded();

      if (!isMounted || !containerRef.current) return;

      if (!window.turnstile) {
        console.warn('[Turnstile] Script failed to load or was blocked.');
        setStatus('error');
        setErrorMessage('Security challenge script blocked by browser or ad blocker.');
        onError?.('script_load_failed');
        return;
      }

      // If already rendered, remove previous instance before re-rendering
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore cleanup notice
        }
        widgetIdRef.current = null;
      }

      // Clear container HTML
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      try {
        const options: TurnstileRenderOptions = {
          sitekey: siteKey,
          theme,
          size,
          action,
          cData,
          callback: (token: string) => {
            if (!isMounted) return;
            setStatus('verified');
            setErrorMessage(null);
            onSuccess(token);
          },
          'error-callback': (code?: string) => {
            if (!isMounted) return;
            console.warn('[Turnstile] Cloudflare challenge error code:', code);
            setStatus('error');
            setErrorMessage('Security verification error. Please click Retry.');
            onError?.(code);
          },
          'expired-callback': () => {
            if (!isMounted) return;
            setStatus('expired');
            setErrorMessage('Verification expired. Please check again.');
            onExpire?.();
          },
          'timeout-callback': () => {
            if (!isMounted) return;
            setStatus('error');
            setErrorMessage('Verification timed out. Please click Retry.');
            onError?.('timeout');
          }
        };

        const id = window.turnstile.render(containerRef.current, options);
        widgetIdRef.current = id;
        setStatus('ready');
      } catch (err: unknown) {
        console.error('[Turnstile] Render error:', err);
        if (isMounted) {
          setStatus('error');
          setErrorMessage('Could not initialize verification challenge.');
          onError?.(err instanceof Error ? err.message : 'render_error');
        }
      }
    };

    renderWidget();

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore unmount cleanup
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, size, action, cData]);

  const handleManualRetry = () => {
    if (window.turnstile && widgetIdRef.current) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        setStatus('ready');
        setErrorMessage(null);
      } catch {
        setStatus('loading');
      }
    } else {
      setStatus('loading');
    }
  };

  return (
    <div className={`turnstile-widget-wrapper flex flex-col items-center justify-center gap-2.5 p-3.5 rounded-2xl bg-neutral-950/90 border border-neutral-800 transition-all ${className}`}>
      <div className="flex items-center justify-between w-full text-[11px] font-mono text-slate-400 px-1">
        <div className="flex items-center gap-1.5">
          {status === 'verified' ? (
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          ) : (
            <Shield className="w-4 h-4 text-brand-400" />
          )}
          <span className={status === 'verified' ? 'text-emerald-300 font-semibold' : 'text-slate-300'}>
            {status === 'verified' 
              ? 'Human Verification Confirmed' 
              : 'Spam & Abuse Protection'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 uppercase tracking-wider">
          <span>Cloudflare Turnstile</span>
        </div>
      </div>

      {/* Target Container for Cloudflare Turnstile iframe */}
      <div 
        ref={containerRef} 
        className="w-full flex items-center justify-center min-h-[65px] transition-opacity" 
      />

      {/* Status Notifications */}
      {status === 'loading' && (
        <div className="text-[11px] text-slate-400 flex items-center gap-2 animate-pulse">
          <RefreshCw className="w-3 h-3 animate-spin text-brand-400" />
          <span>Verifying security challenge...</span>
        </div>
      )}

      {/* Error state with retry button */}
      {errorMessage && (
        <div className="w-full p-2.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-1.5">
            <TriangleAlert className="w-3.5 h-3.5 text-red-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
          <button
            type="button"
            onClick={handleManualRetry}
            className="px-2.5 py-1 rounded bg-neutral-900 hover:bg-neutral-800 text-slate-200 text-[10px] font-mono font-bold flex items-center gap-1 cursor-pointer shrink-0"
          >
            <RefreshCw className="w-2.5 h-2.5" />
            Retry
          </button>
        </div>
      )}

      {status === 'verified' && (
        <div className="w-full flex items-center justify-between text-[10px] text-emerald-400/90 font-mono px-1">
          <span className="flex items-center gap-1">
            <CircleCheck className="w-3 h-3 text-emerald-400" />
            <span>Security token active</span>
          </span>
          <span className="text-slate-500">Zero tracking cookies</span>
        </div>
      )}
    </div>
  );
};

export default TurnstileWidget;
